import React, { FC, useMemo, useState } from "react";
import { LayoutChangeEvent } from "react-native";
import { ScrollView, View, Image, ALIGN } from "@lookiero/aurora";
import { Text } from "@lookiero/sty-psp-ui";
import { ProductPreview } from "../../../../domain/projections/ProductPreview";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import { cdnImageUrl } from "../../shared/util/cdnImageUrl";
import { financial } from "../../shared/util/financial/financial";
import { capitalizeFirstChar } from "../../shared/util/string";
import { TouchableWithoutFeedback } from "../touchableWithoutFeedback/TouchableWithoutFeedback";
import { IMAGE_ASPECT_RATIO, IMAGE_CDN_WIDTH, style as productGalleryStyle } from "./ProductGallery.styles";

interface ProductGalleryProps {
  readonly title: string;
  readonly products: ProductPreview[];
  readonly onPressProduct: (product: ProductPreview) => void;
}

const ProductGallery: FC<ProductGalleryProps> = ({ title, products, onPressProduct }) => {
  const formatCurrency = useFormatCurrency();
  const [imageWidth, setImageWidth] = useState<number>(0);

  const style = useMemo(() => productGalleryStyle(), []);

  return (
    <View style={style.wrapper}>
      <Text level={2} style={style.title} variant="heading">
        {title}
      </Text>

      <ScrollView contentContainerStyle={style.imagesWrapper} horizontal>
        <View style={[style.imagesContainer, products.length < 5 && { justifyContent: ALIGN.CENTER }]}>
          {products.map((product: ProductPreview, index: number) => {
            const lastProduct = index === products.length - 1;
            const imageGap = style.imageGap;
            const isProductInSales = product.price.discountedPrice && product.price.discountedPrice.percentage > 0;

            return (
              <TouchableWithoutFeedback
                key={product.id}
                testID={`image-${product.id}`}
                style={[
                  style.item,
                  // @ts-ignore
                  {
                    marginRight: !lastProduct ? imageGap : 0,
                    flexBasis: `calc(20% - ${imageGap}px)`,
                  },
                ]}
                onPress={() => onPressProduct(product)}
              >
                <Image
                  alt={product.title}
                  resizeMode="cover"
                  src={cdnImageUrl({ imageUrl: product.imageUrl, width: IMAGE_CDN_WIDTH })}
                  style={[
                    {
                      height: imageWidth * IMAGE_ASPECT_RATIO,
                      width: "100%",
                    },
                    style.image,
                  ]}
                  onLayout={(event: LayoutChangeEvent) => {
                    const { width } = event.nativeEvent.layout;
                    setImageWidth(width);
                  }}
                />

                <View style={style.productInfo}>
                  <Text level={2} style={style.productBrand} variant="detail">
                    {capitalizeFirstChar(product.brand.toLowerCase())}
                  </Text>

                  <Text ellipsizeMode="tail" level={2} variant="detail">
                    {product.title}
                  </Text>

                  {!isProductInSales ? (
                    <Text level={2} variant="detail">
                      {formatCurrency({ amount: financial(product.price.amount) })}
                    </Text>
                  ) : (
                    <View>
                      <View style={style.priceContent}>
                        <Text
                          level={2}
                          style={style.priceWithDiscount}
                          testID="product-discounted-price"
                          variant="detail"
                        >
                          {product.price.discountedPrice &&
                            formatCurrency({ amount: financial(product.price.discountedPrice.amount) })}
                        </Text>

                        <Text level={2} style={style.priceLineThrough} testID="product-price" variant="detail">
                          {formatCurrency({ amount: financial(product.price.amount) })}
                        </Text>

                        <Text style={style.discount}>{`-${product.price.discountedPrice?.percentage}%`}</Text>
                      </View>
                    </View>
                  )}
                </View>
              </TouchableWithoutFeedback>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};

export { ProductGallery };
